import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSnackbar } from "notistack";

import { PhoneField, isValidPhone, toE164 } from "/imports/ui/components/shared";
import api from "/imports/api/client";

// A stored number that already carries a dialling code is a partial the client
// can finish. Bare digits are not: prefilling them against the club's country
// would put "+971" in front of a number that might be British, and a client who
// trusts the prefill saves a number the club can never reach. Those are shown as
// text instead, so the digits are there to copy but the country is theirs to pick.
const prefillFrom = (stored) => ((stored || "").trim().startsWith("+") ? stored.trim() : "");

function PhoneConfirmDialog({ open, slug, storedPhone, onClose, onSaved }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();

  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setPhone(prefillFrom(storedPhone));
      setError(null);
    }
  }, [open, storedPhone]);

  const unusable = (storedPhone || "").trim();
  const showsStored = !!unusable && !prefillFrom(unusable);

  const handleSave = async () => {
    if (!isValidPhone(phone)) {
      setError("Please enter a valid mobile number, including its country code.");
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const { client } = await api.confirmPhone(slug, toE164(phone));
      enqueueSnackbar("Phone number saved", { variant: "success" });
      onSaved(client);
    } catch (err) {
      setError(err.message || "We couldn't save your number. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
        {unusable ? "Confirm your mobile number" : "Add your mobile number"}
        <IconButton onClick={onClose} disabled={submitting} sx={{ position: "absolute", right: 8, top: 8 }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {unusable
            ? "We couldn't verify the number we have for you, so your club may not be able to reach you about bookings. Please confirm it below."
            : "Your club uses this to reach you about your bookings — reminders, changes and cancellations."}
        </Typography>

        {showsStored && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            We have <strong>{unusable}</strong> on file. Pick your country, then enter the number.
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <PhoneField label="Mobile number" value={phone} onChange={setPhone} disabled={submitting} autoFocus />

        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="textSecondary">
            Include your country code, so reminders reach you wherever you are. Once saved, contact the club to change it.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions disableSpacing sx={{ px: 3, pb: 3, pt: 0, flexDirection: "column", alignItems: "stretch" }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSave}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ py: 1.1, textTransform: "none", fontWeight: 700, color: "common.white", boxShadow: "none" }}
        >
          {submitting ? "Saving…" : "Save number"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PhoneConfirmDialog;
